import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import type { FC } from 'react';

// eslint-disable-next-line import/no-unresolved
import type { Player } from '@schibsted-svp/web-player';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';

import { usePlayerEvent } from '../components/utils/usePlayerEvent';
import { getAssetAspectRatio } from '../shared/helpers/assetHelpers';

import { playerContainer } from './playerContainer';
import { getPlayer } from './playerService';
import { usePlayer } from './hooks/usePlayer';
import { patchPlayerAsset } from './playerHelpers';

const playNext = async (...args: Parameters<Player['playNext']>) => {
  const player = await getPlayer();

  const [asset, data] = args;

  player.playNext(
    // passing vendor manually is required to fix tracking for mixed vendors, e.g. playing
    // assets from brandstudio newsroom using player initialized with vgtv provider
    typeof asset === 'number' ? asset : patchPlayerAsset(asset),
    data
  );
};

async function openAsset({
  asset,
  seekTime,
}: {
  asset: SvpAssetAdditional;
  seekTime?: number;
}) {
  const player = await getPlayer();
  const currentAssetId = player.getRawAsset()?.id;

  if (asset.id !== currentAssetId) {
    player.once('play adPlay', playerContainer.show);
    player.once('assetError', playerContainer.show);

    await playNext(asset, {
      time: seekTime,
    });

    // clean up all local listeners when a next video has been opened
    player.once('play adPlay', () => {
      player.off('play adPlay', playerContainer.show);
      player.off('assetError', playerContainer.show);
    });
  } else {
    playerContainer.show();
  }
}

/**
 * Stops video playback and hides the player container.
 */
async function stopPlayback() {
  const player = await getPlayer();
  try {
    await player.stop();
  } catch {
    // since player.stop() causes autoplay next to fail on iOS
    player.pause();
  }
}

/**
 * Starts video playback and shows the player container.
 *
 * The forceAssetId argument is used to start playback of the live asset that has been preloaded but the token might have already be invalid
 * this option can be removed as soon as https://github.schibsted.io/svp/web-player/issues/1025 is complete
 */
async function startPlayback({
  forceAsset,
  startTime,
}: {
  forceAsset?: SvpAssetAdditional;
  startTime?: number;
} = {}) {
  const player = await getPlayer();
  try {
    if (forceAsset) {
      await playNext(forceAsset, { time: startTime });
    } else {
      player.play();
    }
  } catch {}
}

async function seekToTime(seconds: number) {
  const player = await getPlayer();
  player.seek(seconds);
}

const playerContextValue = {
  openAsset,
  stopPlayback,
  seekToTime,
  startPlayback,
  isPlaying: false,
  isCasting: false,
};

const PlayerContext = createContext(playerContextValue);

export const PlayerProvider: FC = ({ children }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isCasting, setIsCasting] = useState(false);
  const player = usePlayer();
  const assetAspectRatio = getAssetAspectRatio(
    player?.getRawAsset() ?? window.ASSET
  );

  useEffect(() => {
    try {
      if (player?.getState() !== 'adPlaying') {
        playerContainer.setAspectRatio(assetAspectRatio);
      }
    } catch {}
  }, [assetAspectRatio, player]);

  const setPlaying = useCallback(() => setIsPlaying(true), []);
  const setStopped = useCallback(() => setIsPlaying(false), []);

  const handleCasting = useCallback(({ active }: jwplayer.CastParam) => {
    setIsCasting(active);
  }, []);

  usePlayerEvent('play adPlay', setPlaying);
  usePlayerEvent('pause adPause stop', setStopped);
  usePlayerEvent('cast', handleCasting);

  const value = { ...playerContextValue, isPlaying, isCasting };

  return (
    <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>
  );
};

export function usePlayerContext() {
  return useContext(PlayerContext);
}
